/**
 * GlobeMapProvider - Provider component for globe/map state management.
 *
 * Manages unified state for:
 * - View position (center, zoom, heading)
 * - Mode (browse vs trip-detail)
 * - Curvature animation
 * - Markers
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import { GlobeMapContext } from './GlobeMapContext'
import { GLOBE_CONFIG } from '../constants'
import { getVisibleTiles, zoomToFov, fovToVisibleSpan, normalizeLongitude } from '../utils/tileCalculations'
import type {
  GlobeMapState,
  GlobeMapContextValue,
  GlobeMapInitialState,
  GlobeMarker,
  AnimateOptions,
  CameraAnimationTarget,
} from '../types'
import { lerp, lerpAngle, easingFunctions, type GeoLocation, type GeoBounds } from '@/domain/geo'

interface GlobeMapProviderProps {
  children: ReactNode
  /** Initial state configuration */
  initialState?: GlobeMapInitialState
}

/**
 * Provider component for globe/map state.
 *
 * Wraps the application and provides state and operations to all descendants.
 *
 * @example
 * ```tsx
 * <GlobeMapProvider>
 *   <GlobeCanvas />
 *   <TripListOverlay />
 * </GlobeMapProvider>
 * ```
 */
export function GlobeMapProvider({
  children,
  initialState,
}: GlobeMapProviderProps) {
  // Core state
  // Default center: New York City
  const [state, setState] = useState<GlobeMapState>({
    center: initialState?.center ?? { latitude: 40.7128, longitude: -74.006 },
    zoom: initialState?.zoom ?? GLOBE_CONFIG.DEFAULT_ZOOM,
    heading: initialState?.heading ?? 0,
    mode: initialState?.mode ?? 'browse',
    selectedTripId: initialState?.selectedTripId ?? null,
    markers: initialState?.markers ?? [],
    curvature: GLOBE_CONFIG.CURVATURE_BROWSE,
    animating: false,
    cameraAnimation: null,
    maxTileZoom: 6,
    autoPanPaused: false,
    lastInteractionTime: null,
  })

  // Animation frame reference for curvature animation
  const animationFrameRef = useRef<number | null>(null)
  // Animation frame reference for camera animation
  const cameraAnimationFrameRef = useRef<number | null>(null)
  // Animation frame reference for auto-pan
  const autoPanFrameRef = useRef<number | null>(null)
  // Track current curvature for animation without triggering effect re-runs
  const curvatureRef = useRef(state.curvature)

  // Derived: FOV from zoom
  const fov = useMemo(() => zoomToFov(state.zoom), [state.zoom])

  // Derived: Target curvature based on mode only
  // Browse mode uses full hemisphere curvature - the visual "zoom" comes from FOV
  // Trip-detail mode flattens to show map tiles more like a traditional map
  const targetCurvature = useMemo(
    () =>
      state.mode === 'browse'
        ? GLOBE_CONFIG.CURVATURE_BROWSE
        : GLOBE_CONFIG.CURVATURE_DETAIL,
    [state.mode]
  )

  // Derived: Visible tiles - updates continuously with animation
  // Using state.zoom directly (not a stable ref) so tiles update during animation
  // When animating, also include target tiles for pre-caching
  const visibleTiles = useMemo(() => {
    const currentTiles = getVisibleTiles(
      state.center.latitude,
      state.center.longitude,
      fov,
      state.zoom,
      state.maxTileZoom
    )

    // If animating to a target, also include destination tiles for pre-caching
    if (state.cameraAnimation?.center || state.cameraAnimation?.zoom !== undefined) {
      const targetCenter = state.cameraAnimation.center ?? state.center
      const targetZoom = state.cameraAnimation.zoom ?? state.zoom
      const targetFov = zoomToFov(targetZoom)

      // Merge tiles, avoiding duplicates by key
      const tileMap = new Map<string, typeof currentTiles[0]>()
      for (const tile of currentTiles) {
        const key = `${tile.z}/${tile.x}/${tile.y}@${tile.renderOffset ?? 0}`
        tileMap.set(key, tile)
      }

      // Add target tiles at the destination zoom
      const targetTiles = getVisibleTiles(
        targetCenter.latitude,
        targetCenter.longitude,
        targetFov,
        targetZoom,
        state.maxTileZoom
      )
      for (const tile of targetTiles) {
        const key = `${tile.z}/${tile.x}/${tile.y}@${tile.renderOffset ?? 0}`
        if (!tileMap.has(key)) {
          tileMap.set(key, tile)
        }
      }

      return Array.from(tileMap.values())
    }

    return currentTiles
  }, [state.center.latitude, state.center.longitude, fov, state.zoom, state.maxTileZoom, state.cameraAnimation])

  // Curvature animation effect
  useEffect(() => {
    // Cancel any existing animation first to avoid race conditions
    // when targetCurvature changes while an animation is in progress
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    const epsilon = 0.001

    const animate = () => {
      setState((prev) => {
        const diff = Math.abs(prev.curvature - targetCurvature)

        if (diff < epsilon) {
          curvatureRef.current = targetCurvature
          return prev.animating
            ? { ...prev, curvature: targetCurvature, animating: false }
            : prev
        }

        const newCurvature = lerp(
          prev.curvature,
          targetCurvature,
          GLOBE_CONFIG.CURVATURE_LERP_FACTOR
        )

        curvatureRef.current = newCurvature
        return {
          ...prev,
          curvature: newCurvature,
          animating: true,
        }
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    // Use ref to check if animation needed (avoids effect re-runs during animation)
    if (Math.abs(curvatureRef.current - targetCurvature) >= epsilon) {
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [targetCurvature])

  // Camera animation effect
  useEffect(() => {
    if (!state.cameraAnimation) return

    const animate = () => {
      setState((prev) => {
        if (!prev.cameraAnimation) return prev

        const elapsed = performance.now() - prev.cameraAnimation.startTime
        const t = Math.min(1, elapsed / prev.cameraAnimation.duration)
        const easedT = prev.cameraAnimation.easing(t)

        const { startValues, center: targetCenter, zoom: targetZoom, heading: targetHeading } = prev.cameraAnimation

        // Interpolate values
        const newCenter = targetCenter
          ? {
              latitude: lerp(startValues.center.latitude, targetCenter.latitude, easedT),
              // Use lerpAngle for longitude to handle dateline correctly
              // lerpAngle returns 0-360, normalizeLongitude converts to -180...180
              longitude: normalizeLongitude(
                lerpAngle(startValues.center.longitude, targetCenter.longitude, easedT)
              ),
            }
          : prev.center

        const newZoom = targetZoom !== undefined
          ? lerp(startValues.zoom, targetZoom, easedT)
          : prev.zoom

        const newHeading = targetHeading !== undefined
          ? lerpAngle(startValues.heading, targetHeading, easedT)
          : prev.heading

        // Animation complete
        if (t >= 1) {
          return {
            ...prev,
            center: targetCenter ?? prev.center,
            zoom: targetZoom ?? prev.zoom,
            heading: targetHeading ?? prev.heading,
            cameraAnimation: null,
          }
        }

        return {
          ...prev,
          center: newCenter,
          zoom: newZoom,
          heading: newHeading,
        }
      })

      cameraAnimationFrameRef.current = requestAnimationFrame(animate)
    }

    cameraAnimationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (cameraAnimationFrameRef.current !== null) {
        cancelAnimationFrame(cameraAnimationFrameRef.current)
        cameraAnimationFrameRef.current = null
      }
    }
  }, [state.cameraAnimation])

  // Auto-pan effect - subtle west-to-east pan in browse mode
  // Uses refs to avoid re-running effect on every state change
  const autoPanStateRef = useRef({ paused: false, lastInteraction: 0 })

  // Keep ref in sync with state
  useEffect(() => {
    autoPanStateRef.current.paused = state.autoPanPaused
    autoPanStateRef.current.lastInteraction = state.lastInteractionTime ?? 0
  }, [state.autoPanPaused, state.lastInteractionTime])

  useEffect(() => {
    // Only auto-pan in browse mode
    if (state.mode !== 'browse') {
      if (autoPanFrameRef.current !== null) {
        cancelAnimationFrame(autoPanFrameRef.current)
        autoPanFrameRef.current = null
      }
      return
    }

    // Don't auto-pan during camera animations
    if (state.cameraAnimation) return

    const AUTO_PAN_SPEED = 1.0 // Degrees per second (west to east)
    const AUTO_PAN_RESUME_DELAY = 5000 // 5 seconds

    let lastTime = performance.now()

    const animate = () => {
      const now = performance.now()
      const deltaTime = (now - lastTime) / 1000 // Convert to seconds
      lastTime = now

      const { paused, lastInteraction } = autoPanStateRef.current

      // Check if paused
      if (paused) {
        // Check if enough time has passed to resume
        const timeSinceInteraction = now - lastInteraction
        if (timeSinceInteraction >= AUTO_PAN_RESUME_DELAY) {
          // Unpause
          setState((prev) => ({
            ...prev,
            autoPanPaused: false,
            lastInteractionTime: null,
          }))
        }
        // Continue loop but don't pan
        autoPanFrameRef.current = requestAnimationFrame(animate)
        return
      }

      // Pan east (increase longitude)
      const deltaLng = AUTO_PAN_SPEED * deltaTime
      setState((prev) => ({
        ...prev,
        center: {
          ...prev.center,
          longitude: normalizeLongitude(prev.center.longitude + deltaLng),
        },
      }))

      autoPanFrameRef.current = requestAnimationFrame(animate)
    }

    autoPanFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (autoPanFrameRef.current !== null) {
        cancelAnimationFrame(autoPanFrameRef.current)
        autoPanFrameRef.current = null
      }
    }
  }, [state.mode, state.cameraAnimation])

  // Helper to clamp latitude and normalize longitude
  const normalizeCoordinates = useCallback(
    (lat: number, lng: number) => {
      const halfVisibleLat = fovToVisibleSpan(fov) / 4
      const maxAllowedLat = GLOBE_CONFIG.MAX_LATITUDE - halfVisibleLat
      const clampedLat = Math.max(-maxAllowedLat, Math.min(maxAllowedLat, lat))
      return { latitude: clampedLat, longitude: normalizeLongitude(lng) }
    },
    [fov]
  )

  // Helper to start a camera animation
  const startCameraAnimation = useCallback(
    (
      target: { center?: GeoLocation; zoom?: number; heading?: number },
      options?: AnimateOptions
    ) => {
      const duration = options?.duration ?? 1000
      const easingName = options?.easing ?? 'ease-out'
      const easing = easingFunctions[easingName] ?? easingFunctions['ease-out']

      setState((prev) => ({
        ...prev,
        cameraAnimation: {
          ...target,
          startTime: performance.now(),
          duration,
          easing,
          startValues: {
            center: prev.center,
            zoom: prev.zoom,
            heading: prev.heading,
          },
        },
      }))
    },
    []
  )

  // Operations

  const cancelAnimation = useCallback(() => {
    if (cameraAnimationFrameRef.current !== null) {
      cancelAnimationFrame(cameraAnimationFrameRef.current)
      cameraAnimationFrameRef.current = null
    }
    setState((prev) => ({
      ...prev,
      cameraAnimation: null,
    }))
  }, [])

  const pauseAutoPan = useCallback(() => {
    setState((prev) => ({
      ...prev,
      autoPanPaused: true,
      lastInteractionTime: performance.now(),
    }))
  }, [])

  const panTo = useCallback(
    (lat: number, lng: number, options?: AnimateOptions) => {
      const target = normalizeCoordinates(lat, lng)

      if (options?.duration && options.duration > 0) {
        startCameraAnimation({ center: target }, options)
      } else {
        // Instant (cancel any in-progress animation)
        cancelAnimation()
        setState((prev) => ({
          ...prev,
          center: target,
        }))
      }
    },
    [normalizeCoordinates, startCameraAnimation, cancelAnimation]
  )

  const setZoom = useCallback(
    (zoom: number, options?: AnimateOptions) => {
      const clampedZoom = Math.max(
        GLOBE_CONFIG.MIN_ZOOM,
        Math.min(GLOBE_CONFIG.MAX_ZOOM, zoom)
      )

      if (options?.duration && options.duration > 0) {
        startCameraAnimation({ zoom: clampedZoom }, options)
      } else {
        cancelAnimation()
        setState((prev) => ({
          ...prev,
          zoom: clampedZoom,
        }))
      }
    },
    [startCameraAnimation, cancelAnimation]
  )

  const flyTo = useCallback(
    (lat: number, lng: number, zoom: number, options?: AnimateOptions) => {
      const target = normalizeCoordinates(lat, lng)
      const clampedZoom = Math.max(
        GLOBE_CONFIG.MIN_ZOOM,
        Math.min(GLOBE_CONFIG.MAX_ZOOM, zoom)
      )

      const animOptions: AnimateOptions = {
        duration: options?.duration ?? 1500,
        easing: options?.easing ?? 'ease-in-out',
      }

      startCameraAnimation({ center: target, zoom: clampedZoom }, animOptions)
    },
    [normalizeCoordinates, startCameraAnimation]
  )

  /**
   * Set the camera heading/rotation.
   * Heading is normalized to 0-360 degrees (0 = north, 90 = east).
   * Animation uses lerpAngle() to ensure the shortest rotation path.
   */
  const setHeading = useCallback(
    (heading: number, options?: AnimateOptions) => {
      const normalizedHeading = ((heading % 360) + 360) % 360

      if (options?.duration && options.duration > 0) {
        startCameraAnimation({ heading: normalizedHeading }, options)
      } else {
        cancelAnimation()
        setState((prev) => ({
          ...prev,
          heading: normalizedHeading,
        }))
      }
    },
    [startCameraAnimation, cancelAnimation]
  )

  const selectTrip = useCallback(
    (tripId: string, destination?: GeoLocation) => {
      setState((prev) => ({
        ...prev,
        mode: 'trip-detail',
        selectedTripId: tripId,
        center: destination ?? prev.center,
        zoom: Math.max(prev.zoom, 8), // Zoom in when selecting trip
      }))
    },
    []
  )

  const deselectTrip = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mode: 'browse',
      selectedTripId: null,
      zoom: Math.min(prev.zoom, GLOBE_CONFIG.DEFAULT_ZOOM), // Zoom out when deselecting
    }))
  }, [])

  const addMarker = useCallback((marker: GlobeMarker) => {
    setState((prev) => ({
      ...prev,
      markers: [...prev.markers, marker],
    }))
  }, [])

  const removeMarker = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      markers: prev.markers.filter((m) => m.id !== id),
    }))
  }, [])

  const setMarkers = useCallback((markers: GlobeMarker[]) => {
    setState((prev) => ({
      ...prev,
      markers,
    }))
  }, [])

  const setMaxTileZoom = useCallback((maxZoom: number) => {
    setState((prev) => ({
      ...prev,
      maxTileZoom: Math.max(1, Math.min(10, maxZoom)),
    }))
  }, [])

  const fitBounds = useCallback((bounds: GeoBounds) => {
    // Calculate center from bounds
    const centerLat = (bounds.north + bounds.south) / 2
    const centerLng = (bounds.east + bounds.west) / 2

    // Calculate appropriate zoom level
    const latSpan = bounds.north - bounds.south
    const lngSpan = bounds.east - bounds.west
    const maxSpan = Math.max(latSpan, lngSpan)

    // Rough zoom calculation based on span
    // 360 degrees = zoom 0, halves each level
    const zoom = Math.floor(Math.log2(360 / maxSpan))
    const clampedZoom = Math.max(
      GLOBE_CONFIG.MIN_ZOOM,
      Math.min(GLOBE_CONFIG.MAX_ZOOM, zoom)
    )

    setState((prev) => ({
      ...prev,
      center: { latitude: centerLat, longitude: centerLng },
      zoom: clampedZoom,
    }))
  }, [])

  // Combine into context value
  const value = useMemo<GlobeMapContextValue>(
    () => ({
      // State
      ...state,
      // Derived
      fov,
      targetCurvature,
      visibleTiles,
      // Operations
      panTo,
      setZoom,
      flyTo,
      cancelAnimation,
      pauseAutoPan,
      setHeading,
      selectTrip,
      deselectTrip,
      addMarker,
      removeMarker,
      setMarkers,
      setMaxTileZoom,
      fitBounds,
    }),
    [
      state,
      fov,
      targetCurvature,
      visibleTiles,
      panTo,
      setZoom,
      flyTo,
      cancelAnimation,
      pauseAutoPan,
      setHeading,
      selectTrip,
      deselectTrip,
      addMarker,
      removeMarker,
      setMarkers,
      setMaxTileZoom,
      fitBounds,
    ]
  )

  return (
    <GlobeMapContext.Provider value={value}>
      {children}
    </GlobeMapContext.Provider>
  )
}
